import CircledFeatures from "@/config/userInteraction/circledFeaturesLayerConfig";
import FocusAreasLayerConfig from "@/config/urbanDesignLayers/focusAreasLayerConfig";
import { buildingLayersConfigs } from "@/config/urbanDesignLayers/buildingLayersConfigs";
import LandscapeLayerConfig from "@/config/urbanDesignLayers/landscapeLayerConfig";
import { SourceAndLayerConfigs, StoreState } from "@/models";
import {
  addSourceAndLayerToMap,
  addDeckLayerToMap,
  hideBuildingUseColors,
  hideLayers,
} from "@/services/map.service";
import {
  buildDesignGlbLayer,
  buildDesignGlbLayerForOverlay,
} from "@/services/designGlb.service";
import {
  isInjectMode,
  addDesignLayerToInjectDeck,
} from "@/services/deckOverlay.service";
import CityPyO from "@/store/cityPyO";
import { ActionContext } from "vuex";

export default {
  async createDesignLayers({ state }: ActionContext<StoreState, StoreState>) {
    const emptyGeoJson = { type: "FeatureCollection" as const, features: [] };
    const designConfigs = [...buildingLayersConfigs, LandscapeLayerConfig];
    // Add building/landscape layers first (await so order is deterministic)
    await Promise.all(
      designConfigs.map(async (config: SourceAndLayerConfigs) => {
        const layerData = await state.cityPyO.getLayer(config.source.id);
        config.source.options.data = layerData ?? emptyGeoJson;
        addSourceAndLayerToMap(config.source, config.layerConfigs, state.map);
        hideBuildingUseColors(state.map);
      })
    );

    // Add user design GLB if design_origin.json and design.glb exist
    try {
      const designOrigin = await state.cityPyO.getDesignOrigin();
      if (designOrigin && state.map) {
        const glbUrl = state.cityPyO.getDesignGlbUrl();
        const preCheck = await fetch(glbUrl, { method: "HEAD" });
        if (process.env.NODE_ENV === "development") {
          console.log(
            "Design GLB pre-check:",
            preCheck.ok ? "OK" : "FAILED",
            preCheck.status,
            glbUrl
          );
        }
        if (!preCheck.ok) {
          // Don't add layer when design.glb is missing – avoids errors and wrong checkbox state
          return;
        }

        if (isInjectMode(state.map)) {
          const layer = buildDesignGlbLayerForOverlay(
            glbUrl,
            designOrigin.origin,
            designOrigin.sizeScale ?? 10
          );
          addDesignLayerToInjectDeck(state.map, layer);
        } else {
          const layer = buildDesignGlbLayer(
            glbUrl,
            designOrigin.origin,
            designOrigin.sizeScale ?? 10
          );
          addDeckLayerToMap(layer, state.map);
        }
        // Debug layer – uncomment to show red box at design origin
        // const debugLayer = buildDesignGlbDebugLayer(designOrigin.origin);
        // addDeckLayerToMap(debugLayer, state.map);
        if (process.env.NODE_ENV === "development") {
          console.log("Design GLB layer added at", designOrigin.origin, glbUrl);
        }
      }
    } catch (e) {
      // design_origin or design.glb missing – skip silently
      if (process.env.NODE_ENV === "development") {
        console.debug("Design GLB layer skipped:", e);
      }
    }
  },
  addFocusAreasMapLayer({
    state,
    commit,
  }: ActionContext<StoreState, StoreState>) {
    const emptyGeoJson = { type: "FeatureCollection" as const, features: [] };
    state.cityPyO.getLayer("focusAreas").then((geojson) => {
      commit("focusAreasGeoJson", geojson);
      FocusAreasLayerConfig.source.options.data = geojson ?? emptyGeoJson;
      addSourceAndLayerToMap(
        FocusAreasLayerConfig.source,
        FocusAreasLayerConfig.layerConfigs,
        state.map
      );
      hideLayers(
        state.map,
        FocusAreasLayerConfig.layerConfigs.map((conf) => {
          return conf.id;
        })
      );
    });
  },
  // TODO do this in layer service?
  // eigentlich musst du nur den highlighted layer anzeigen.

  editFeatureProps({ state }, feature) {
    if (feature) {
      try {
        const sourceId = feature.layer.source;
        const source = state.map.getSource(sourceId);
        const sourceData = source?._data;
        const sourceFeatures = Array.isArray(sourceData)
          ? sourceData
          : sourceData.features;
        const sourceFeature = sourceFeatures.find(
          (sf) => parseInt(sf.id, 10) === feature.id
        );

        if (sourceFeature && typeof sourceFeature === "object") {
          sourceFeature.properties = feature.properties;
        }
        source.setData(sourceData);
      } catch (e) {
        console.warn("Could not find feature match in raw data", e);
      }
    }
  },
  async connect(
    { state, commit, dispatch }: ActionContext<StoreState, StoreState>,
    options: ConnectionOptions
  ) {
    const cityPyo = new CityPyO();
    const authResponse = await cityPyo.login(options.userdata);

    if (authResponse.authenticated) {
      commit("cityPyO", cityPyo);
    }

    return authResponse;
  },

  updateCircledFeaturesLayer(
    { state, commit, dispatch }: ActionContext<StoreState, StoreState>,
    featureBuffer
  ) {
    const featureCircles = state.featureCircles;

    let bufferIndex = null;
    featureCircles.some((circle, index) => {
      if (
        circle.properties["objectId"] === featureBuffer.properties["objectId"]
      ) {
        bufferIndex = index;
        return true;
      }
    });

    // add or remove current featureBuffer from featureCircles
    if (bufferIndex === null) {
      featureCircles.push(featureBuffer);
    } else {
      featureCircles.splice(bufferIndex, 1);
    }

    // update layer on map
    const source = CircledFeatures.source;
    source.options.data.features = featureCircles;
    addSourceAndLayerToMap(source, CircledFeatures.layerConfigs, state.map);
    commit("featureCircles", featureCircles);
  },
};
